/**
 * Video Carousel Rail — native engine (replaces jQuery Slick for the
 * video-carousel section).
 *
 * Model: a horizontal rail with a centered active tile and graduated
 * "amphitheatre" wings (data-tier="0..3" by distance from center, mapped
 * to tile heights in the section CSS). Two modes, chosen by content width:
 *
 *  LOOP (enough content to recycle — e.g. mobile, where tiles are 68% wide):
 *    infinite DOM-recycle ring (same idea as ProductScroller.maintainLoop —
 *    real nodes reordered, no clones), but the ring is rebalanced ONLY AT
 *    REST. Native CSS snap + momentum own every user-driven scroll frame;
 *    writing scrollLeft mid-gesture is what used to fight iOS momentum
 *    tracking and send the rail haywire (ProductScroller learned the same
 *    lesson — its ring is reserved for auto-drift, never user scrolling).
 *    On scroll-idle the DOM is recentered around the settled tile with
 *    compensated scrollLeft writes, which is visually invisible and
 *    restores runway on both sides for the next fling.
 *
 *  CLAMPED (everything ~visible at once — e.g. desktop 5-per-row with 5
 *    tiles): native CSS scroll-snap does the settling, the rail clamps at
 *    its real ends (no empty runway), the tier profile radiates from the
 *    nearest-to-center tile, and timed autoplay ping-pongs instead of
 *    wrapping so there is never a long jump.
 *
 * Programmatic centering (tap / autoplay / arrows / dots) is an eased rAF
 * tween with CSS snap suspended for its duration — native smooth scrolling
 * and mandatory snap hijack each other (double-settle stutter; see
 * ProductScroller.animateScroll, which hit the same conflict) — and any
 * touch cancels the tween instantly so it can never fight the finger.
 *
 * Interaction (owner-specified): tap a non-center tile -> it glides to
 * center, expands, reveals its product card (card + mute pill are
 * center-only); tap the center video to pause/resume; hover never changes
 * a tile's height. The center video plays muted by default; on fine-pointer
 * desktops an inactive video can also cross-fade in silently on hover.
 *
 * Idioms shared with responsive-video-loader.js / ProductScroller:
 * IntersectionObserver gating, rAF-coalesced scroll work, passive
 * listeners, prefers-reduced-motion -> no autoplay + instant jumps,
 * idempotent against theme-editor re-renders (custom element lifecycle).
 */
(function () {
    'use strict';

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var mobileViewport = window.matchMedia('(max-width: 749px)');
    var hoverPreviewViewport = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    var MAX_TIER = 3;
    var SELECTION_DURATION = 320;
    var HEIGHT_DURATION = 200;
    var HEIGHT_DELAY = SELECTION_DURATION - HEIGHT_DURATION;
    var HEIGHT_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

    class VideoCarouselRail extends HTMLElement {
        connectedCallback() {
            if (this._init) return; // editor re-attach of the same node
            this._init = true;

            this.rail = this.querySelector('[data-vcr-rail]');
            if (!this.rail || this.rail.children.length < 2) return;

            // "Shuffle order" setting: Fisher-Yates the tiles before anything
            // measures or centers, so every visit opens on different products.
            if (this.hasAttribute('data-random')) {
                var pool = Array.from(this.rail.children);
                for (var i = pool.length - 1; i > 0; i--) {
                    var j = Math.floor(Math.random() * (i + 1));
                    var t = pool[i]; pool[i] = pool[j]; pool[j] = t;
                }
                pool.forEach((el) => this.rail.appendChild(el));
            }

            this.centerEl = null;
            this.loopable = false;
            this.pitch = 0;
            this._holding = false;
            this._moving = false;
            this._ticking = false;
            this._selecting = false;
            this._selectionToken = 0;
            this.abort = new AbortController();
            this._slidesMs = (parseFloat(this.getAttribute('data-slides-speed')) || 2) * 1000;

            this.bindEvents();
            if (this.hasAttribute('data-dots')) this.buildDots();
            if (this.hasAttribute('data-arrows')) this.buildArrows();
            if (this.hasAttribute('data-autoplay') && !reduceMotion.matches) this.setupAutoplay();

            this._ro = new ResizeObserver(() => { this.measure(); this.updateTiers(); });
            this._ro.observe(this.rail);

            this.measure();
            var kids = this.items();
            this.centerTo(kids[Math.floor(kids.length / 2)], true);
            this.updateTiers();
        }

        disconnectedCallback() {
            this._init = false;
            if (this.abort) this.abort.abort();
            if (this._ro) this._ro.disconnect();
            if (this._io) this._io.disconnect();
            clearInterval(this._autoTimer);
            clearTimeout(this._settleT);
            clearTimeout(this._playT);
            this.cancelSelection(false);
            if (this._raf) cancelAnimationFrame(this._raf);
            if (this._tweenRAF) cancelAnimationFrame(this._tweenRAF);
            if (this.rail) this.items().forEach((item) => {
                this.stopSlides(item);
                this.stopHoverPreview(item);
                this.cancelVideoFrameWait(item, item.querySelector('video'));
            });
        }

        items() { return Array.from(this.rail.children); }

        /* ---- geometry / loop ring --------------------------------------- */

        measure() {
            var kids = this.items();
            kids.forEach((item) => {
                item.style.scrollSnapStop = mobileViewport.matches ? 'always' : '';
            });
            this.pitch = kids.length > 1 ? (kids[1].offsetLeft - kids[0].offsetLeft) : 0;
            // one buffer each side + a band wider than one tile, or the two
            // recycle loops fight each other (same threshold as ProductScroller)
            this.loopable = this.pitch > 0 &&
                this.rail.scrollWidth >= this.rail.clientWidth + this.pitch * 3;
            this.classList.toggle('vcr-host--loop', this.loopable);
            // a resize back to a scrollable rail returns focus to geometry
            if (this._pinned && !this.cantScroll()) this._pinned = null;
            if (this.loopable && this.rail.scrollLeft < this.pitch) {
                this.rail.scrollLeft = this.pitch; // seed left runway
            }
            this.rebalance();
        }

        // Recenter the ring's DOM around `focus` (default: the current center)
        // with a compensated scrollLeft write — the viewport never moves, the
        // runway on both sides is restored. Called ONLY at rest (scroll-idle,
        // or immediately before a tween starts): recycling during a gesture is
        // exactly what broke native touch scrolling. The focus node itself is
        // never moved, so its playing video is undisturbed.
        // The write is absolute, from the focus tile's measured position:
        // Chrome re-snaps a mandatory-snap rail to its snapped tile when a
        // sibling moves, so a relative `scrollLeft -= pitch` per move
        // compensated twice (autoplay jumped back a tile, then slid two).
        rebalance(focus) {
            if (!this.loopable || !this.pitch) return;
            var r = this.rail;
            var kids = this.items();
            var anchor = focus || this.centerEl;
            var ci = kids.indexOf(anchor);
            if (ci === -1) return;
            var mid = Math.floor(kids.length / 2);
            if (ci === mid) return;
            var x = anchor.offsetLeft - r.scrollLeft;
            var guard = kids.length;
            while (ci > mid && guard-- > 0) {
                r.appendChild(r.firstElementChild);
                ci--;
            }
            while (ci < mid && guard-- > 0) {
                r.insertBefore(r.lastElementChild, r.firstElementChild);
                ci++;
            }
            r.scrollLeft = anchor.offsetLeft - x;
        }

        onScroll() {
            if (!this._ticking) {
                this._ticking = true;
                this._raf = requestAnimationFrame(() => {
                    this._ticking = false;
                    if (!this._selecting) this.updateTiers(); // visual only — no scrollLeft writes
                });
            }
            // Settle work runs only once scrolling is fully at rest: rebalance
            // the ring, and re-center only if a long fling clamped at the
            // rail's REAL edge (the one spot native snap cannot center).
            this._moving = true;
            clearTimeout(this._settleT);
            this._settleT = setTimeout(() => {
                this._moving = false;
                if (this._tweenRAF || this._holding || this._selecting) return;
                this.updateTiers();
                var gestureTarget = this.takeGestureTarget();
                if (gestureTarget && gestureTarget !== this.centerEl) {
                    this.centerTo(gestureTarget);
                    return;
                }
                if (!this.loopable || !this.centerEl) return;
                this.rebalance();
                var c = this.centerEl;
                var delta = (c.offsetLeft + c.offsetWidth / 2) -
                            (this.rail.scrollLeft + this.rail.clientWidth / 2);
                if (Math.abs(delta) > 1) this.tweenTo(c);
                else this.playCenter(); // re-assert after any node moves
            }, 150);
        }

        updateTiers() {
            if (this._selecting) return;
            var kids = this.items();
            var nearest = null;

            if (this._pinned && kids.indexOf(this._pinned) !== -1) {
                // pinned focus (rail can't scroll — e.g. desktop showing every
                // tile): the tapped tile is the center; tiers radiate by index
                nearest = this._pinned;
                var pi = kids.indexOf(nearest);
                kids.forEach((item, i) => {
                    var tier = String(Math.min(MAX_TIER, Math.abs(i - pi)));
                    if (item.dataset.tier !== tier) item.dataset.tier = tier;
                });
            } else {
                var mid = this.rail.scrollLeft + this.rail.clientWidth / 2;
                var nearestDist = Infinity;
                var pitch = this.pitch || 1;
                kids.forEach((item) => {
                    var d = Math.abs(item.offsetLeft + item.offsetWidth / 2 - mid);
                    if (d < nearestDist) { nearestDist = d; nearest = item; }
                    var tier = String(Math.min(MAX_TIER, Math.round(d / pitch)));
                    if (item.dataset.tier !== tier) item.dataset.tier = tier;
                });
            }

            if (nearest && nearest !== this.centerEl) {
                if (this.centerEl) {
                    var previousVideo = this.centerEl.querySelector('video');
                    var previousPoster = this.centerEl.querySelector('.vcr-poster');
                    this.cancelVideoFrameWait(this.centerEl, previousVideo);
                    if (previousPoster) previousPoster.style.opacity = '1';
                    this.centerEl.classList.remove('is-center', 'is-card-active', 'vcr-buffering', 'vcr-media-ready');
                }
                if (this._hoverItem === nearest) this.stopHoverPreview(nearest);
                var nearestPoster = nearest.querySelector('.vcr-poster');
                if (nearestPoster) nearestPoster.style.opacity = '1';
                nearest.classList.add('is-center');
                this.setCardActive(nearest);
                this.centerEl = nearest;
                // debounced: a glide can flip the center several times in
                // flight, and the resulting play()/pause() bursts raced into
                // a paused final state — only the SETTLED center plays
                clearTimeout(this._playT);
                this._playT = setTimeout(() => this.playCenter(), 140);
                this.updateDots();
            }
        }

        takeGestureTarget() {
            var start = this._gestureStartEl;
            var previous = this._gesturePreviousEl;
            var next = this._gestureNextEl;
            this._gestureStartEl = null;
            this._gesturePreviousEl = null;
            this._gestureNextEl = null;
            if (!start || !this.centerEl) return null;

            var kids = this.items();
            var startIndex = kids.indexOf(start);
            var settledIndex = kids.indexOf(this.centerEl);
            if (startIndex === -1 || settledIndex === -1) return null;
            if (settledIndex > startIndex) return next || start;
            if (settledIndex < startIndex) return previous || start;
            return start;
        }

        // True when the rail has no meaningful overflow (every tile visible)
        // — scrolling can't center anything, so focus must move instead.
        cantScroll() {
            return this.rail.scrollWidth - this.rail.clientWidth < (this.pitch || 1) / 2;
        }

        centerTo(item, instant) {
            if (!item) return;
            if (this._selecting) this.cancelSelection(true);
            if (this.cantScroll()) {
                // nothing to scroll — move the FOCUS to the tapped tile instead
                this._pinned = item;
                this.updateTiers();
                return;
            }
            this._pinned = null;
            if (instant || reduceMotion.matches) {
                this.rebalance(item); // runway first, so the jump can't clamp
                this.rail.scrollLeft += (item.offsetLeft + item.offsetWidth / 2) -
                                        (this.rail.scrollLeft + this.rail.clientWidth / 2);
                this.updateTiers();
            } else {
                this.tweenTo(item);
            }
        }

        // Eased center tween for taps / autoplay / arrows. CSS snap is
        // suspended for the duration (native smooth + mandatory snap hijack
        // each other) and restored on completion or cancel; a rebalance right
        // before takeoff guarantees runway to the target, and none happens
        // mid-flight, so the target can never teleport under the tween.
        tweenTo(item) {
            this.cancelTween();
            this.rebalance(item);
            var rail = this.rail;
            var delta = (item.offsetLeft + item.offsetWidth / 2) -
                        (rail.scrollLeft + rail.clientWidth / 2);
            if (Math.abs(delta) < 1) { this.updateTiers(); return; }

            rail.style.scrollSnapType = 'none';
            var duration = 450;
            var ease = function (t) { return 1 - Math.pow(1 - t, 3); };
            var elapsed = 0, lastTs = null, applied = 0;
            var self = this;
            var step = function (ts) {
                if (lastTs === null) lastTs = ts;
                elapsed += ts - lastTs;
                lastTs = ts;
                var target = delta * ease(Math.min(1, elapsed / duration));
                rail.scrollLeft += target - applied;
                applied = target;
                if (elapsed < duration) {
                    self._tweenRAF = requestAnimationFrame(step);
                } else {
                    self._tweenRAF = null;
                    rail.style.scrollSnapType = '';
                    self.updateTiers();
                    self.rebalance();
                    self.playCenter(); // re-assert playback after the glide
                }
            };
            this._tweenRAF = requestAnimationFrame(step);
        }

        // A touch mid-tween hands control straight back to the finger; snap
        // is restored so the interrupted gesture still settles natively.
        cancelTween() {
            if (!this._tweenRAF) return;
            cancelAnimationFrame(this._tweenRAF);
            this._tweenRAF = null;
            this.rail.style.scrollSnapType = '';
        }

        /* ---- interaction (delegated — survives ring reorders) ------------ */

        bindEvents() {
            var signal = { signal: this.abort.signal };

            this.rail.addEventListener('scroll', this.onScroll.bind(this),
                { passive: true, signal: this.abort.signal });

            // capture phase: a tap on a NON-center tile's MEDIA centers it.
            // Clicks inside a card pass through untouched — a hidden card is
            // pointer-events:none (so its area still centers), and a card made
            // visible by the merchant's always/hover modes keeps working as
            // plain product links for quick browsing.
            this.rail.addEventListener('click', (e) => {
                var item = e.target.closest('.video-item');
                if (!item || item === this.centerEl) return;
                if (e.target.closest('.video-content-wrapper')) return;
                e.preventDefault();
                e.stopPropagation();
                this.holdAutoplay();
                this.selectItem(item);
            }, { capture: true, signal: this.abort.signal });

            this.rail.addEventListener('click', (e) => {
                var mute = e.target.closest('.video-toggle-mute');
                var item = e.target.closest('.video-item');
                if (!item || item !== this.centerEl) return;
                var video = item.querySelector('video');
                if (!video) return;
                if (mute) {
                    video.muted = !video.muted;
                    mute.classList.toggle('is-sound-on', !video.muted);
                    return;
                }
                if (e.target.closest('.video-content-wrapper')) return; // card links work normally
                this.holdAutoplay();
                if (video.paused) { this.playVideo(item, video); }
                else {
                    video.pause();
                    this.cancelVideoFrameWait(item, video);
                    item.classList.remove('vcr-buffering');
                }
            }, signal);

            this.rail.addEventListener('pointerover', (e) => {
                var wrapper = e.target.closest('.video-wrapper');
                if (!wrapper || (e.relatedTarget && wrapper.contains(e.relatedTarget))) return;
                this.startHoverPreview(wrapper.closest('.video-item'));
            }, signal);

            this.rail.addEventListener('pointerout', (e) => {
                var wrapper = e.target.closest('.video-wrapper');
                if (!wrapper || (e.relatedTarget && wrapper.contains(e.relatedTarget))) return;
                this.stopHoverPreview(wrapper.closest('.video-item'));
            }, signal);

            ['pointerdown', 'touchstart'].forEach((ev) =>
                this.rail.addEventListener(ev, (e) => {
                    if (this._holding) return;
                    var hoverItem = e.target.closest('.video-item');
                    if (hoverItem) this.stopHoverPreview(hoverItem);
                    if (this._selecting) this.cancelSelection(true);
                    this._holding = true;
                    if (mobileViewport.matches) {
                        this._gestureStartEl = this.centerEl;
                        this._gesturePreviousEl = this.centerEl && this.centerEl.previousElementSibling;
                        this._gestureNextEl = this.centerEl && this.centerEl.nextElementSibling;
                    }
                    this.cancelTween(); // never fight the finger
                    this.holdAutoplay();
                }, { passive: true, signal: this.abort.signal }));
            ['pointerup', 'pointercancel', 'touchend'].forEach((ev) =>
                this.rail.addEventListener(ev, () => {
                    if (!this._holding) return;
                    this._holding = false;
                    if (this._gestureStartEl || this.loopable) this.onScroll(); // arm the settle
                }, { passive: true, signal: this.abort.signal }));
        }

        async selectItem(item) {
            if (!item || item === this.centerEl) return;
            if (this._selecting) this.cancelSelection(true);
            if (reduceMotion.matches) {
                this.centerTo(item, true);
                return;
            }

            clearTimeout(this._settleT);
            clearTimeout(this._playT);
            this.cancelTween();
            this._gestureStartEl = null;
            this._gesturePreviousEl = null;
            this._gestureNextEl = null;
            this._selecting = true;
            this._moving = true;
            var token = ++this._selectionToken;
            var outgoing = this.centerEl;
            this.prepareSelectionItem(item);
            var outgoingPoster = outgoing && outgoing.querySelector('.vcr-poster');
            if (outgoingPoster) outgoingPoster.style.opacity = '1';

            this.rebalance(item);
            this._selectionItems = this.items();
            var moveResults = await Promise.all([
                this.animateSelectionScroll(item, token),
                this.animateSelectionTiers(item, token, this.cantScroll() ? 0 : HEIGHT_DELAY)
            ]);
            if (moveResults.some((completed) => !completed) || !this.selectionIsActive(token)) return;

            var outgoingVideo = outgoing && outgoing.querySelector('video');
            if (outgoingVideo && !outgoingVideo.paused) outgoingVideo.pause();
            if (outgoing) {
                this.cancelVideoFrameWait(outgoing, outgoingVideo);
                this.stopSlides(outgoing);
                outgoing.classList.remove('is-center', 'vcr-buffering', 'vcr-media-ready');
            }

            var incomingPoster = item.querySelector('.vcr-poster');
            if (incomingPoster) incomingPoster.style.opacity = '1';
            item.classList.add('is-center');
            this.centerEl = item;
            this._pinned = this.cantScroll() ? item : null;
            this._selectionItems.forEach((selectionItem) => this.clearItemMotion(selectionItem));
            this.rail.style.scrollSnapType = '';
            this._selectionItems = null;
            this._selecting = false;
            this._moving = false;
            this.updateDots();
            this.playCenter();
        }

        animateSelectionScroll(item, token) {
            var rail = this.rail;
            rail.style.scrollSnapType = 'none';
            if (this.cantScroll()) return Promise.resolve(true);
            var delta = (item.offsetLeft + item.offsetWidth / 2) -
                        (rail.scrollLeft + rail.clientWidth / 2);
            if (Math.abs(delta) < 1) return Promise.resolve(true);

            return new Promise((resolve) => {
                var duration = SELECTION_DURATION;
                var elapsed = 0;
                var lastTs = null;
                var applied = 0;
                var finish = (value) => {
                    this._selectionRAF = null;
                    this._selectionResolve = null;
                    resolve(value);
                };
                this._selectionResolve = finish;
                var step = (ts) => {
                    if (!this.selectionIsActive(token)) {
                        finish(false);
                        return;
                    }
                    if (lastTs === null) lastTs = ts;
                    elapsed += ts - lastTs;
                    lastTs = ts;
                    var progress = Math.min(1, elapsed / duration);
                    var eased = 1 - Math.pow(1 - progress, 3);
                    var target = delta * eased;
                    rail.scrollLeft += target - applied;
                    applied = target;
                    if (progress < 1) this._selectionRAF = requestAnimationFrame(step);
                    else finish(true);
                };
                this._selectionRAF = requestAnimationFrame(step);
            });
        }

        animateSelectionTiers(target, token, delay) {
            return this.waitSelectionPhase(delay, token).then((active) => {
                if (!active) return false;
                this.setCardActive(target);
                var kids = this.items();
                var targetIndex = kids.indexOf(target);
                return Promise.all(kids.map((item, index) => {
                    var tier = Math.min(MAX_TIER, Math.abs(index - targetIndex));
                    return this.animateItemTier(item, tier, token, HEIGHT_DURATION);
                })).then((results) => results.every((completed) => completed));
            });
        }

        animateItemTier(item, tier, token, duration) {
            if (!item) return Promise.resolve(true);
            var nodes = this.motionNodes(item);
            nodes.forEach((node) => {
                node.style.transitionDuration = duration + 'ms';
                node.style.transitionDelay = '0ms';
                node.style.transitionTimingFunction = HEIGHT_EASING;
            });
            void item.offsetHeight;
            item.dataset.tier = String(tier);
            item.style.setProperty('--vcr-f', this.tierFactor(tier));
            return this.waitSelectionPhase(duration, token).then((active) => {
                if (active) nodes.forEach((node) => {
                    node.style.removeProperty('transition-duration');
                    node.style.removeProperty('transition-delay');
                    node.style.removeProperty('transition-timing-function');
                });
                return active;
            });
        }

        waitSelectionPhase(duration, token) {
            return new Promise((resolve) => {
                setTimeout(() => resolve(this.selectionIsActive(token)), duration);
            });
        }

        selectionIsActive(token) {
            return this._selecting && token === this._selectionToken;
        }

        setCardActive(activeItem) {
            this.items().forEach((item) => {
                item.classList.toggle('is-card-active', item === activeItem);
            });
        }

        tierFactor(tier) {
            return ['1', '0.86', '0.74', '0.62'][Math.min(MAX_TIER, tier)];
        }

        motionNodes(item) {
            if (!item) return [];
            return Array.from(item.querySelectorAll('.video-section__media, .vcr-poster'));
        }

        prepareSelectionItem(item) {
            var poster = item.querySelector('.vcr-poster');
            var firstSlide = item.querySelector('.vcr-slides img.is-on');
            var video = item.querySelector('video');
            if (poster) {
                poster.loading = 'eager';
                poster.style.opacity = '1';
            }
            if (firstSlide) firstSlide.loading = 'eager';
            if (video) {
                video.preload = 'auto';
                if (video.networkState === video.NETWORK_EMPTY) video.load();
            }
        }

        clearItemMotion(item) {
            if (!item) return;
            item.style.removeProperty('--vcr-f');
            this.motionNodes(item).forEach((node) => {
                node.style.removeProperty('transition-duration');
                node.style.removeProperty('transition-delay');
                node.style.removeProperty('transition-timing-function');
            });
        }

        cancelSelection(reconcile) {
            if (!this._selecting) return;
            this._selecting = false;
            this._moving = false;
            this._selectionToken++;
            if (this._selectionRAF) cancelAnimationFrame(this._selectionRAF);
            this._selectionRAF = null;
            if (this._selectionResolve) this._selectionResolve(false);
            this._selectionResolve = null;
            this.rail.style.scrollSnapType = '';
            (this._selectionItems || []).forEach((item) => this.clearItemMotion(item));
            this._selectionItems = null;
            if (!reconcile) return;

            clearTimeout(this._playT);
            this.items().forEach((item) => {
                var video = item.querySelector('video');
                var poster = item.querySelector('.vcr-poster');
                if (video && !video.paused) video.pause();
                this.cancelVideoFrameWait(item, video);
                this.stopSlides(item);
                if (poster) poster.style.opacity = '1';
                item.classList.remove('is-center', 'is-card-active', 'vcr-buffering', 'vcr-media-ready');
            });
            this.centerEl = null;
            this._pinned = null;
            this.updateTiers();
        }

        playCenter() {
            this.prepareVideos();
            this.items().forEach((item) => {
                var video = item.querySelector('video');
                var slides = item.querySelector('.vcr-slides');
                if (item === this.centerEl) {
                    if (video) {
                        if (video.paused) this.playVideo(item, video);
                        else if (!item.classList.contains('vcr-media-ready')) {
                            this.waitForVideoFrame(item, video);
                        }
                    }
                    if (slides) this.startSlides(item, slides);
                } else {
                    if (video && !video.paused && item !== this._hoverItem) video.pause();
                    this.cancelVideoFrameWait(item, video);
                    item.classList.remove('vcr-buffering', 'vcr-media-ready');
                    this.stopSlides(item);
                }
            });
        }

        prepareVideos() {
            var center = this.centerEl;
            if (!center) return;
            var adjacent = [center.previousElementSibling, center.nextElementSibling];
            this.items().forEach((item) => {
                var nearby = item === center || adjacent.indexOf(item) !== -1;
                var poster = item.querySelector('.vcr-poster');
                var firstSlide = item.querySelector('.vcr-slides img.is-on');
                if (nearby && poster) poster.loading = 'eager';
                if (nearby && firstSlide) firstSlide.loading = 'eager';

                var video = item.querySelector('video');
                if (!video) return;
                var preload = item === center ? 'auto' : (nearby ? 'metadata' : 'none');
                if (video.preload === preload) return;
                video.preload = preload;
                if (preload !== 'none' && video.networkState === video.NETWORK_EMPTY) video.load();
            });
        }

        playVideo(item, video) {
            if (!item.classList.contains('vcr-media-ready')) {
                if (video.readyState < 3) item.classList.add('vcr-buffering');
                this.waitForVideoFrame(item, video);
            }
            video.play().catch(() => {
                this.cancelVideoFrameWait(item, video);
                item.classList.remove('vcr-buffering');
            });
        }

        waitForVideoFrame(item, video) {
            this.cancelVideoFrameWait(item, video);
            var reveal = () => {
                if (item !== this.centerEl) return;
                item._vcrFrameHandle = null;
                item._vcrFrameRAF = null;
                item._vcrLoadedData = null;
                item.classList.remove('vcr-buffering');
                item.classList.add('vcr-media-ready');
                var poster = item.querySelector('.vcr-poster');
                if (poster) poster.style.opacity = '0';
            };

            if (typeof video.requestVideoFrameCallback === 'function') {
                item._vcrFrameHandle = video.requestVideoFrameCallback(reveal);
            } else if (video.readyState >= 2) {
                item._vcrFrameRAF = requestAnimationFrame(reveal);
            } else {
                item._vcrLoadedData = () => {
                    item._vcrLoadedData = null;
                    item._vcrFrameRAF = requestAnimationFrame(reveal);
                };
                video.addEventListener('loadeddata', item._vcrLoadedData, { once: true });
            }
        }

        cancelVideoFrameWait(item, video) {
            if (!item) return;
            if (video && item._vcrFrameHandle !== null && item._vcrFrameHandle !== undefined &&
                typeof video.cancelVideoFrameCallback === 'function') {
                video.cancelVideoFrameCallback(item._vcrFrameHandle);
            }
            if (item._vcrFrameRAF) cancelAnimationFrame(item._vcrFrameRAF);
            if (video && item._vcrLoadedData) video.removeEventListener('loadeddata', item._vcrLoadedData);
            item._vcrFrameHandle = null;
            item._vcrFrameRAF = null;
            item._vcrLoadedData = null;
        }

        startHoverPreview(item) {
            if (!item || item === this.centerEl || this._selecting || reduceMotion.matches ||
                !hoverPreviewViewport.matches) return;
            var video = item.querySelector('video');
            var poster = item.querySelector('.vcr-poster');
            if (!video || !poster) return;
            if (this._hoverItem === item) return;
            if (this._hoverItem) this.stopHoverPreview(this._hoverItem);

            this._hoverItem = item;
            item._vcrHoverWasMuted = video.muted;
            video.muted = true;
            video.preload = 'auto';
            if (video.networkState === video.NETWORK_EMPTY) video.load();

            video.play().then(() => {
                if (this._hoverItem !== item) return;
                var show = () => {
                    item._vcrHoverFrameHandle = null;
                    item._vcrHoverFrameRAF = null;
                    item._vcrHoverLoadedData = null;
                    if (this._hoverItem !== item) return;
                    item.classList.add('vcr-hover-preview');
                    poster.style.opacity = '0';
                };
                if (typeof video.requestVideoFrameCallback === 'function') {
                    item._vcrHoverFrameHandle = video.requestVideoFrameCallback(show);
                } else if (video.readyState >= 2) {
                    item._vcrHoverFrameRAF = requestAnimationFrame(show);
                } else {
                    item._vcrHoverLoadedData = show;
                    video.addEventListener('loadeddata', show, { once: true });
                }
            }).catch(() => this.stopHoverPreview(item));
        }

        stopHoverPreview(item) {
            if (!item) return;
            var isPreview = this._hoverItem === item || item.classList.contains('vcr-hover-preview') ||
                (item._vcrHoverFrameHandle !== null && item._vcrHoverFrameHandle !== undefined) ||
                item._vcrHoverFrameRAF || item._vcrHoverLoadedData;
            if (!isPreview) return;
            if (this._hoverItem === item) this._hoverItem = null;
            var video = item.querySelector('video');
            var poster = item.querySelector('.vcr-poster');
            if (item._vcrHoverFrameHandle !== null && item._vcrHoverFrameHandle !== undefined && video &&
                typeof video.cancelVideoFrameCallback === 'function') {
                video.cancelVideoFrameCallback(item._vcrHoverFrameHandle);
            }
            if (item._vcrHoverFrameRAF) cancelAnimationFrame(item._vcrHoverFrameRAF);
            if (video && item._vcrHoverLoadedData) video.removeEventListener('loadeddata', item._vcrHoverLoadedData);
            item._vcrHoverFrameHandle = null;
            item._vcrHoverFrameRAF = null;
            item._vcrHoverLoadedData = null;
            if (video) {
                if (item !== this.centerEl && !video.paused) video.pause();
                if (item._vcrHoverWasMuted !== undefined) video.muted = item._vcrHoverWasMuted;
            }
            item._vcrHoverWasMuted = undefined;
            item.classList.remove('vcr-hover-preview');
            if (poster) poster.style.opacity = '1';
        }

        /* ---- no-video tiles: cross-fade the product photos (center only) -- */

        startSlides(item, slides) {
            if (item._slidesT || reduceMotion.matches) return;
            var imgs = slides.querySelectorAll('img');
            if (imgs.length < 2) return;
            item._slidesT = setInterval(function () {
                if (document.hidden) return;
                var on = 0;
                for (var i = 0; i < imgs.length; i++) {
                    if (imgs[i].classList.contains('is-on')) { on = i; break; }
                }
                imgs[on].classList.remove('is-on');
                imgs[(on + 1) % imgs.length].classList.add('is-on');
            }, this._slidesMs);
        }

        stopSlides(item) {
            if (!item._slidesT) return;
            clearInterval(item._slidesT);
            item._slidesT = null;
        }

        /* ---- timed advance ---------------------------------------------- */

        setupAutoplay() {
            this._speed = parseInt(this.getAttribute('data-autoplay-speed'), 10) || 4000;
            this._inView = false;
            this._hover = false;
            this._dir = 1;

            this._io = new IntersectionObserver((entries) => {
                this._inView = entries[0].isIntersecting;
            }, { threshold: 0.3 });
            this._io.observe(this);

            this.addEventListener('pointerenter', () => { this._hover = true; },
                { signal: this.abort.signal });
            this.addEventListener('pointerleave', () => { this._hover = false; },
                { signal: this.abort.signal });

            this.startAutoplay();
        }

        startAutoplay() {
            clearInterval(this._autoTimer);
            this._autoTimer = setInterval(() => {
                // _moving: a user fling may still be decelerating — advancing
                // now would rebalance/tween against live momentum
                if (!this._inView || this._hover || this._holding || this._moving || this._selecting ||
                    document.hidden || !this.centerEl) return;
                if (this.loopable) {
                    // the ring guarantees a neighbour on both sides
                    this.centerTo(this.centerEl.nextElementSibling || this.items()[0]);
                } else {
                    // clamped: ping-pong so the ends never produce a long jump
                    var kids = this.items();
                    var i = kids.indexOf(this.centerEl);
                    if (i + this._dir >= kids.length || i + this._dir < 0) this._dir = -this._dir;
                    this.centerTo(kids[i + this._dir]);
                }
            }, this._speed);
        }

        // a deliberate tap restarts the clock so the carousel doesn't yank
        // the visitor's chosen tile away a moment later
        holdAutoplay() {
            if (this._autoTimer) this.startAutoplay();
        }

        /* ---- nav (reuses the theme's slick-dots / slick-arrow styling) -- */
        /* dots index the DOM order, which the ring rotates — so dots are
           shown in clamped mode only (CSS hides them on .vcr-host--loop) */

        buildDots() {
            var ul = document.createElement('ul');
            ul.className = 'slick-dots vcr-dots';
            if (!this.hasAttribute('data-dots-desktop')) ul.classList.add('vcr-dots--d-off');
            if (!this.hasAttribute('data-dots-mobile')) ul.classList.add('vcr-dots--m-off');
            this.items().forEach((item, i) => {
                var li = document.createElement('li');
                var b = document.createElement('button');
                b.type = 'button';
                b.textContent = i + 1;
                b.setAttribute('aria-label', 'Go to video ' + (i + 1));
                b.addEventListener('click', () => {
                    this.holdAutoplay();
                    this.centerTo(this.items()[i]);
                }, { signal: this.abort.signal });
                li.appendChild(b);
                ul.appendChild(li);
            });
            this.dotsEl = ul;
            this.appendChild(ul);
        }

        updateDots() {
            if (!this.dotsEl || this.loopable) return;
            var kids = this.items();
            var cur = kids.indexOf(this.centerEl);
            var lis = this.dotsEl.children;
            for (var i = 0; i < lis.length; i++) {
                lis[i].classList.toggle('slick-active', i === cur);
            }
        }

        buildArrows() {
            var make = (dir) => {
                var markup = dir === 'prev'
                    ? (window.arrows && window.arrows.icon_prev)
                    : (window.arrows && window.arrows.icon_next);
                var holder = document.createElement('div');
                holder.innerHTML = markup || '<button type="button" class="slick-' + dir + '" aria-label="' + dir + '"></button>';
                var btn = holder.firstElementChild;
                btn.classList.add('slick-arrow', 'vcr-arrow', 'vcr-arrow--' + dir);
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.holdAutoplay();
                    var target = dir === 'prev'
                        ? this.centerEl && this.centerEl.previousElementSibling
                        : this.centerEl && this.centerEl.nextElementSibling;
                    if (target) this.centerTo(target);
                }, { signal: this.abort.signal });
                return btn;
            };
            if (!this.hasAttribute('data-arrows-desktop')) this.classList.add('vcr-arrows--d-off');
            if (!this.hasAttribute('data-arrows-mobile')) this.classList.add('vcr-arrows--m-off');
            this.append(make('prev'), make('next'));
        }
    }

    if (!customElements.get('video-carousel-rail')) {
        customElements.define('video-carousel-rail', VideoCarouselRail);
    }
})();
